import { Fragment } from "react";
import { enmReservationType } from "../../models/enums/ReservationType";
import dayjs from "dayjs";
import cancelImg from '../../assets/img/cancel.jpg';
import newImg from '../../assets/img/new.jpg';
import modifyImage from '../../assets/img/modify.jpg';
import { mdlReservation } from "../../models/domain/Reservation";
import { useTranslation } from "react-i18next";
import _ from 'lodash';
import { mdlCustomer } from "../../models/domain/Customer";
import { getAgencyLogoURL } from "../../utils/S3Helper";

type ReservationVoucherProps = {
    reservation?: mdlReservation;
    reservations?: mdlReservation[];
}

const ReservationVoucher = (props: ReservationVoucherProps) => {

    const { t } = useTranslation();

    const getReservationTypeImage = (resType?: enmReservationType) => {
        switch (resType) {
            case enmReservationType.New:
                return <img src={newImg} />
            case enmReservationType.Cancelled:
                return <img src={cancelImg} />
            default:
                return <img src={modifyImage} />
        }
    }

    const getAgencyAndTourOpName = (reservation: mdlReservation) => {
        switch (reservation.webParameters?.nameShownOnTheResCard) {
            case 1:
                return <span>{reservation.webParameters?.agencyName}</span>
            case 2:
                return <span>{reservation.operator}</span>
            case 3:
                return (
                    <>
                        {reservation.webParameters?.agencyName}
                        <br />
                        {reservation.operator}
                    </>
                )
            default:
                return <></>
        }
    }

    const getVoucherOperatorText = (reservation: mdlReservation) => {
        var voucherOperator: string | undefined = reservation.voucherNo;
        var operator: string | undefined = reservation.operator;
        return voucherOperator + " - " + operator;
    }

    const getFirstCustomersNationality = (reservation: mdlReservation) => {
        if (reservation.customers != null && reservation.customers.length > 0) {
            var firstCustomer = _.first(reservation.customers);
            if (firstCustomer)
                return firstCustomer.nationalityString;
        }
        else
            return "";
    }

    const getAgencyNote = (reservation: mdlReservation) => {
        var note = reservation.note;
        var resInfo = new Array<string>();

        if (reservation.foc == "Y")
            resInfo.push(t("pdf.resToPdf.freeOfCharge"));
        if (reservation.balayiCifti == "Y")
            resInfo.push(t("pdf.resToPdf.honeymoon"));
        if (reservation.promosyonRoom == "Y")
            resInfo.push(t("pdf.resToPdf.promotionRoom"));

        if (resInfo.length > 0)
            note += " " + resInfo.join(", ");
        return note;
    }

    const getLetterHeaderFooterLeft = (reservation: mdlReservation) => {
        if (reservation.webParameters?.useLetterhead) {
            return (
                <>
                    <span>{reservation.webParameters.letterheadFooterLeft1}</span>
                    <br />
                    {reservation.webParameters.letterheadFooterLeft2 && <><span>{reservation.webParameters.letterheadFooterLeft2}</span> <br /></>}
                    {reservation.webParameters.letterheadFooterLeft3 && <><span>{reservation.webParameters.letterheadFooterLeft3}</span></>}

                </>
            );
        }
        return <></>;
    }

    const getLetterHeaderFooterRight = (reservation: mdlReservation) => {
        if (reservation.webParameters?.useLetterhead) {
            return (
                <>
                    <span>{reservation.webParameters.letterheadFooterRight1}</span>
                    <br />
                    {reservation.webParameters.letterheadFooterRight2 && <><span>{reservation.webParameters.letterheadFooterRight2}</span> <br /></>}
                    {reservation.webParameters.letterheadFooterRight3 && <><span>{reservation.webParameters.letterheadFooterRight3}</span></>}

                </>
            );
        }
        return <></>;
    }

    const renderReservationView = (reservation: mdlReservation, index: number) => {
        return (
            <>
                <div className={`container-fluid restopdf ${index > 0 ? "page-break" : ""}`}>
                    <div className="row">
                        <div className="col-2 agency-logo-container">
                            <img src={getAgencyLogoURL(`${reservation.agencyId}.jpg`)} />
                        </div>
                        <div className="col-6">
                            {
                                reservation.webParameters?.useLetterhead &&
                                <h5>{reservation.webParameters?.letterhead}</h5>
                            }
                            <h5>{reservation.hotelName}</h5>
                            <h5>{getAgencyAndTourOpName(reservation)}</h5>
                        </div>
                        <div className="col-4 res-type-container">
                            {
                                getReservationTypeImage(reservation.reservationType)
                            }
                            <span><strong>{t("pdf.resToPdf.resType.label")}: </strong>{t(`pdf.resToPdf.resType.${reservation.reservationType}`)}</span>
                            <span><strong>{t("pdf.resToPdf.exportDate")}: </strong>{dayjs().format("DD.MM.YYYY")}</span>
                            <span><strong>{t("pdf.resToPdf.exportTime")}: </strong>{dayjs().format("HH:mm")}</span>
                        </div>
                    </div>


                    <div className="details-section">
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <div className="col-12">
                                            <strong>{t("pdf.resToPdf.voucher")}</strong>
                                        </div>
                                        <div className="col-12">
                                            {getVoucherOperatorText(reservation)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="col-12">
                                            <strong>{t("pdf.resToPdf.hotelConfirmationStatus.label")}</strong>
                                        </div>
                                        <div className="col-12">
                                            {t(`pdf.resToPdf.hotelConfirmationStatus.${reservation.confirmationStatus}`)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="col-12">
                                            <strong>{t("pdf.resToPdf.quota")}</strong>
                                        </div>
                                        <div className="col-12">
                                            {t(`pdf.resToPdf.quotaType.${reservation.quotaType}`)}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>

                        </table>
                    </div>

                    <div className="details-section">
                        <div className="section-header">
                            <span className="section-text">{t("pdf.resToPdf.reservationInformations")}</span>
                        </div>
                        <table>
                            <tbody>
                                <tr>
                                    <td><strong>{t("pdf.resToPdf.checkInDate")}:</strong> {dayjs(reservation.checkIn).format("DD.MM.YYYY")}</td>
                                    <td><strong>{t("pdf.resToPdf.roomCount")}:</strong> {reservation.roomCount}</td>
                                    <td><strong>{t("pdf.resToPdf.adult")}:</strong> {reservation.adults}</td>
                                </tr>
                                <tr>
                                    <td><strong>{t("pdf.resToPdf.checkOutDate")}:</strong> {dayjs(reservation.checkOut).format("DD.MM.YYYY")}</td>
                                    <td><strong>{t("pdf.resToPdf.accomStyle")}:</strong> {reservation.roomName}</td>
                                    <td><strong>{t("pdf.resToPdf.extraBed")}:</strong> {reservation.extraBed}</td>
                                </tr>
                                <tr>
                                    <td><strong>{t("pdf.resToPdf.night")}:</strong> {reservation.nights}</td>
                                    <td><strong>{t("pdf.resToPdf.roomType")}:</strong> {reservation.roomTypeName}</td>
                                    <td><strong>{t("pdf.resToPdf.child")}:</strong> {reservation.childs}</td>
                                </tr>
                                <tr>
                                    <td><strong>{t("pdf.resToPdf.nationality")}:</strong> {getFirstCustomersNationality(reservation)}</td>
                                    <td><strong>{t("pdf.resToPdf.board")}:</strong> {reservation.accomName}</td>
                                    <td><strong>{t("pdf.resToPdf.infant")}:</strong> {reservation.infants}</td>
                                </tr>
                                <tr>
                                    <td><strong>{t("pdf.resToPdf.sejSequence")}:</strong> {reservation.sequence}</td>
                                    <td><strong>{t("pdf.resToPdf.connection.label")}:</strong> {t(`pdf.resToPdf.connection.${reservation.reservationStatus}`)} </td>
                                    <td><strong>{t("pdf.resToPdf.total")}:</strong> {reservation.pax}</td>
                                </tr>
                                <tr>
                                    <td><strong>{t("pdf.resToPdf.sellDate")}:</strong> {dayjs(reservation.sellDate).format("DD.MM.YYYY")}</td>
                                    <td><strong>{t("pdf.resToPdf.originalHotelCode")}:</strong> {reservation.originalHotelCode}</td>
                                    <td><strong>{t("pdf.resToPdf.receiptNo")}:</strong> {reservation.fisNo}</td>
                                </tr>
                            </tbody>
                            {
                                reservation.webParameters?.showInvoiceHeader
                                &&
                                <tfoot>
                                    <tr>
                                        <td colSpan={3} className="text-center"><strong>{t("pdf.resToPdf.receiptHeader")}: </strong>{reservation.invoiceHeader}</td>
                                    </tr>
                                </tfoot>
                            }
                        </table>
                    </div>

                    <div className="details-section">
                        <div className="section-header"><span className="section-text">{t("pdf.resToPdf.customerInformations")}</span></div>
                        <table>
                            <thead>
                                <tr>
                                    <th>{t("pdf.resToPdf.gender")}</th>
                                    <th>{t("pdf.resToPdf.surnameName")}</th>
                                    <th>{t("pdf.resToPdf.age")}</th>
                                    <th>{t("pdf.resToPdf.birthDate")}</th>
                                    <th>{t("pdf.resToPdf.arrivalLocation")}</th>
                                    <th>{t("pdf.resToPdf.hour")}</th>
                                    <th>{t("pdf.resToPdf.returnLocation")}</th>
                                    <th>{t("pdf.resToPdf.hour")}</th>
                                    <th>{t("pdf.resToPdf.passport")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    reservation.customers?.map((customer: mdlCustomer, cindex: number) => (
                                        <tr key={cindex}>
                                            <td>{customer.title}</td>
                                            <td>{customer.name}</td>
                                            <td>{customer.age}</td>
                                            <td>{dayjs(customer.birthDate).format("DD.MM.YYYY")}</td>
                                            <td>{customer.arrival} - {customer.arrivalYon1}</td>
                                            <td>{customer.arrivalTime ? dayjs(customer.arrivalTime).format("HH:mm") : "-"}</td>
                                            <td>{customer.departure} - {customer.departureYon1}</td>
                                            <td>{customer.departureTime ? dayjs(customer.departureTime).format("HH:mm") : "-"}</td>
                                            <td>{customer.musTakipNo}</td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className="details-section hotel-area">
                        <div className="row">
                            <div className="col-12">
                                <strong>{t("pdf.resToPdf.hotelConfirmationNo")}: {reservation.confirmationNo}</strong>
                            </div>
                            <hr></hr>
                            <div className="col-8">
                                <table>
                                    <tbody>
                                        <tr>
                                            <td><strong>{t("pdf.resToPdf.agencyNote")}:</strong> {getAgencyNote(reservation)}</td>
                                        </tr>
                                        <tr style={{ maxWidth: "100px", textWrap: "wrap" }}>
                                            <td><strong>{t("pdf.resToPdf.hotelNote")}: {reservation.confirmeNote}</strong></td>
                                        </tr>
                                        <tr>
                                            <td><strong>{t("pdf.resToPdf.agencyName")}:</strong> {reservation.webParameters?.agencyName}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>{t("pdf.resToPdf.agencyUser")}:</strong> {reservation.lastChangeUser}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>{t("pdf.resToPdf.sejourHotelName")}:</strong> {reservation.sejHotelName}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="col-4">
                                <table>
                                    <thead>
                                        <tr>
                                            <th className="align-right">{t("pdf.resToPdf.service")}</th>
                                            <th className="align-right">{t("pdf.resToPdf.amount")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="align-right">{t("reservationDetail.accomodation")}</td>
                                            <td className="align-right">{reservation.soldPrice} {reservation.soldCurrency}</td>
                                        </tr>
                                        <tr>
                                            <td className="align-right">{getLetterHeaderFooterLeft(reservation)}</td>
                                            <td className="align-right">{getLetterHeaderFooterRight(reservation)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }


    return (
        <>
            {
                props.reservations
                &&
                props.reservations.length > 0
                &&
                props.reservations.map((reservation: mdlReservation, resIndex: number) => (
                    <Fragment key={resIndex}>
                        {
                            renderReservationView(reservation, resIndex)
                        }
                    </Fragment>
                ))
            }
            {
                props.reservation
                &&
                renderReservationView(props.reservation, 0)
            }
            <style>
                {`
                    .restopdf {
                        padding: 20px;
                        font-size: 10px;
                    }
                    .restopdf h5 {
                        text-align: center;
                        font-size: 14px;
                    }
                    .restopdf table {
                        width: 100%;
                        font-size : inherit;
                    }
                    .restopdf th,
                    .restopdf td {
                        text-align: left;
                        padding: 2px;
                    }
                    .restopdf .details-section {
                        background-color: #f2f2f2;
                    } 
                    .restopdf .align-right {
                        text-align: right;
                    }
                    .restopdf .highlight {
                        color: #2a73d2;
                    } 
                    .restopdf .res-type-container {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-end;
                        font-size: 10px;
                    }
                    .restopdf .res-type-container img {
                        width: 80%;
                    }
                    .table-borderless {
                        border: none !important;
                    }
                    .restopdf .section-header {
                        background-color: #3b5968;
                        color: white;
                        font-weight: bold;
                        text-align: left;
                        display: flex;
                        padding : 2px;
                        font-size : 12px;
                    }
                    .restopdf tr:nth-child(even) {
                        background-color: #d2d5d8;
                    }
                    .restopdf .details-section.hotel-area{
                        margin-top : 15px;
                    }
                    .restopdf .agency-logo-container {
                        display: flex;
                        align-items: flex-start;
                        justify-content: flex-start;
                    }
                    .restopdf .agency-logo {
                        max-width: 120px;
                        max-height: 60px;
                        object-fit: contain;
                    }
                `}
            </style>
        </>
    )
}


export default ReservationVoucher;